// Only import these in browser environment (not during server-side rendering)
import ahoy from "ahoy.js"
import posthog from "posthog-js"
import { initWebVitals } from "./web_vitals"

export { ANALYTICS_EVENT_NAMES, ANALYTICS_EVENTS } from "./events"
export { useModalAnalytics } from "./use_modal_analytics"
export { initWebVitals } from "./web_vitals"

// eslint-disable camelcase
const analytics = {
  // Default service configuration
  defaults: {
    services: {
      posthog: true,
      ga: true,
      ahoy: true,
    },
  },

  init: () => {
    // Skip initialization during server-side rendering
    if (typeof window === "undefined") return

    // Capture Core Web Vitals -> Ahoy (independent of PostHog; runs even without a PostHog key).
    initWebVitals(analytics.track)

    if (!posthog) return

    if (window?.LoveIrish?.apiKeys?.posthog) {
      // On pages whose URL carries a signed token, the server hands us a pre-sanitized path (all
      // masking logic lives server-side in RedactSensitiveUrls — nothing to duplicate here). Use it
      // in place of window.location so the token never reaches PostHog, a third party. Absent on
      // ordinary pages, so normal URLs (incl. client-side navigations) report as-is.
      const sanitizedPath = window?.LoveIrish?.analytics?.sanitizedPath

      posthog.init(window.LoveIrish.apiKeys.posthog, {
        sanitize_properties: (properties) => {
          if (sanitizedPath) {
            if ("$pathname" in properties) {
              properties.$pathname = sanitizedPath.split("?")[0]
            }
            if ("$current_url" in properties) {
              properties.$current_url = window.location.origin + sanitizedPath
            }
          }
          return properties
        },
        loaded: (ph) => {
          // Handle alias inside the loaded callback to ensure PostHog is ready
          try {
            const distinctId = ph.get_distinct_id()
            const sessionId = window.LoveIrish?.analytics?.sessionId
            if (sessionId && distinctId !== sessionId) {
              ph.alias(distinctId, sessionId)
            }
          } catch (error) {
            console.error("PostHog alias failed:", error)
          }
        },
        capture_pageview: true,
        capture_pageleave: true,
        // Disable session recording to prevent timing errors
        // "Called on script loaded before session recording is available"
        disable_session_recording: true,
        autocapture: {
          dom_event_allowlist: ["click", "change", "submit"],
          element_attribute_ignorelist: ["data-no-track", '[type*="password"]'],
        },
        api_host: "https://us.i.posthog.com",
      })
    }
  },

  /**
   * Unified track method - sends to PostHog, Google Analytics, and Ahoy
   * @param {string} event - Event name (use dot.notation for namespacing, e.g., "minigame.started")
   * @param {object} [props] - Event properties
   * @param {object} [options] - Tracking options
   * @param {object|string[]} [options.services] - Services to send to. Can be:
   *   - Object: { posthog: true, ga: false, ahoy: true }
   *   - Array: ["posthog", "ahoy"] (only these services)
   *   - If not provided, uses default configuration
   * @param {string[]} [options.skipServices] - Services to skip (alternative to services object)
   *
   * @example
   * // Send to all services (default) - uses dot notation for namespacing
   * analytics.track("minigame.started", { game_format: "word_guesser" })
   * // Ahoy gets: "minigame.started"
   * // GA gets: "minigame_started" (converted to snake_case)
   * // PostHog gets: "minigame.started"
   *
   * // Skip Ahoy (useful for page views done server-side)
   * analytics.track("$pageview", {}, { skipServices: ["ahoy"] })
   *
   * // Only send to PostHog and Ahoy
   * analytics.track("codex.search", {}, { services: ["posthog", "ahoy"] })
   *
   * // Fine-grained control
   * analytics.track("user.signup", {}, { services: { posthog: true, ga: false, ahoy: true } })
   */
  track: (event, props = {}, options = {}) => {
    // Skip tracking during server-side rendering
    if (typeof window === "undefined") return

    // Determine which services should receive this event
    const serviceConfig = analytics._resolveServiceConfig(options)

    // Send to PostHog (keeps dot notation)
    if (serviceConfig.posthog && posthog) {
      try {
        posthog.capture(event, props)
      } catch (error) {
        console.error("PostHog tracking failed:", error)
      }
    }

    // Send to Google Analytics (converts to snake_case)
    if (serviceConfig.ga) {
      try {
        if (typeof window !== "undefined" && window.gtag) {
          // Convert dot notation to snake_case for GA
          const gaEventName = analytics._toSnakeCase(event)
          // Map common event names to GA4 recommended events
          const mappedEventName = analytics._mapToGAEvent(gaEventName)

          window.gtag("event", mappedEventName, {
            // Flatten nested objects for GA
            ...analytics._flattenProperties(props),
            // Add custom dimension for original event name if mapped
            event_name_original:
              gaEventName !== mappedEventName ? gaEventName : undefined,
          })
        }
      } catch (error) {
        console.error("Google Analytics tracking failed:", error)
      }
    }

    // Send to Ahoy (keeps dot notation for easy namespace querying).
    // Include the sanitized current path so events can be filtered by which page they occur on.
    // (Caller-provided `path` still wins, since props spread after the default.)
    if (serviceConfig.ahoy && ahoy) {
      try {
        ahoy.track(event, { path: analytics._currentPath(), ...props })
      } catch (error) {
        console.error("Ahoy tracking failed:", error)
      }
    }
  },

  trackPageView: () => {
    // Skip Ahoy for page views since they're tracked server-side
    analytics.track("$pageview", {}, { skipServices: ["ahoy"] })
  },

  /**
   *
   * @param label {string} snake_cased string that should have context like blog_post_card when doing a breakdown of link clicks.
   * @param href {url}
   * @param location {[string]} where on the page this components renders (header, footer) that can be used to segment if this link
   * appears more than once on a page
   * @return {CaptureResult | void}
   */
  trackLinkClick: ({ label, href, location = [] }) =>
    analytics.track("clicked#link", {
      // TODO remove these once we have passed the grace period where this is still used
      link_href: href,
      link_label: label,
      link_location: location,
      component_href: href,
      component_label: label,
      component_location: location,
    }),

  trackFormSubmit: ({ label, success, location = [], ...props }) =>
    analytics.track("form#submit", {
      component_label: label,
      component_location: location,
      success,
      ...props,
    }),

  /**
   * Current page path for event properties, sanitized.
   * On pages whose URL carries a signed token, the server hands us a pre-sanitized path
   * (masking lives server-side in RedactSensitiveUrls — nothing to duplicate here). Otherwise
   * use the raw pathname (query string excluded, so no tokens leak). Lets Ahoy events be
   * filtered by which page they occurred on.
   * @private
   */
  _currentPath: () => {
    if (typeof window === "undefined") return undefined
    const sanitized = window?.LoveIrish?.analytics?.sanitizedPath
    return sanitized ? sanitized.split("?")[0] : window.location?.pathname
  },

  /**
   * Convert dot notation to snake_case
   * @private
   * @example
   * "minigame.started" -> "minigame_started"
   * "codex.search" -> "codex_search"
   * "$pageview" -> "$pageview" (special events unchanged)
   */
  _toSnakeCase: (eventName) => {
    // Don't convert special events that start with $ or #
    if (eventName.startsWith("$") || eventName.startsWith("#")) {
      return eventName
    }
    return eventName.replace(/\./g, "_")
  },

  /**
   * Map custom event names to GA4 recommended events
   * After converting from dot notation, we map to GA4 standard events where applicable
   * @private
   */
  _mapToGAEvent: (eventName) => {
    const eventMap = {
      // Codex events (after dot-to-snake conversion: codex.search.submitted -> codex_search_submitted)
      codex_search_submitted: "search",
      codex_search_viewed: "view_search_results",
      // Legacy events
      "clicked#link": "click",
      "form#submit": "form_submit",
      // Add more mappings as needed
    }
    return eventMap[eventName] || eventName
  },

  /**
   * Flatten nested objects for Google Analytics
   * GA doesn't handle nested objects well, so we flatten them
   * @private
   */
  _flattenProperties: (obj, prefix = "") => {
    const flattened = {}

    for (const [key, value] of Object.entries(obj)) {
      const newKey = prefix ? `${prefix}_${key}` : key

      if (value === null || value === undefined) {
        continue
      } else if (Array.isArray(value)) {
        flattened[newKey] = value.join(",")
      } else if (typeof value === "object" && !(value instanceof Date)) {
        Object.assign(flattened, analytics._flattenProperties(value, newKey))
      } else {
        flattened[newKey] = value
      }
    }

    return flattened
  },

  /**
   * Resolve which services should receive this event
   * @private
   */
  _resolveServiceConfig: (options) => {
    // Start with defaults
    const config = { ...analytics.defaults.services }

    // Handle skipServices option (easiest way to skip specific services)
    if (options.skipServices && Array.isArray(options.skipServices)) {
      options.skipServices.forEach((service) => {
        config[service] = false
      })
      return config
    }

    // Handle services option
    if (options.services) {
      // Array format: ["posthog", "ahoy"] means only these services
      if (Array.isArray(options.services)) {
        // Disable all services first
        Object.keys(config).forEach((service) => {
          config[service] = false
        })
        // Enable only specified services
        options.services.forEach((service) => {
          config[service] = true
        })
      }
      // Object format: { posthog: true, ga: false, ahoy: true }
      else if (typeof options.services === "object") {
        Object.assign(config, options.services)
      }
    }

    return config
  },
}
// eslint-enable camelcase

export default analytics
